import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../../axios/api.js';

// Thunks backing the `FollowUpBuying` route (FollowUp). The lead-detail lookup,
// solitaire-company check, move-to-cold-calling, scheduled-date update and the
// review-remark reads are shared with the `FollowUpBiddingBudgetary` route,
// which imports them from here — only the three list endpoints, the country
// filter and the move target differ between the two pages (plain here,
// `-bidding` suffixed there).
//
// The list thunks return rows untransformed: both pages map them through
// component-scoped IST helpers and prefetch remarks before storing.

// Always returns a string. A 500 whose body is `{"error":{"code":..,"detail":..}}`
// would otherwise hand an object to `state.error`, and rendering that object
// takes out the whole page via the error boundary. Picks the first candidate
// that is actually a non-empty string, so an object-shaped `data.error` falls
// through to `data.message` instead of short-circuiting onto the fallback.
const asMessage = (error, fallback) =>
  [error.response?.data?.error, error.response?.data?.message, error.message]
    .find((value) => typeof value === 'string' && value.trim()) || fallback;

// Rejection payload for the three list thunks, which are the only ones whose
// rejection reaches the UI. `message` is safe to render but is NOT rendered:
// the page shows `status` alone, because the upstream string can carry SQL
// fragments and host names. `detail` exists purely for the caller's
// `console.error` and must never be rendered.
const asListError = (error, fallback) => ({
  status: error.response?.status ?? null,
  message: asMessage(error, fallback),
  detail: error.response?.data ?? error.message,
});

// -------------------------------------------------------------- lead lists

export const fetchFollowUpDue = createAsyncThunk(
  'followUp/fetchFollowUpDue',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/clientsscheduled');
      return response.data;
    } catch (error) {
      return rejectWithValue(asListError(error, 'Error fetching data'));
    }
  }
);

export const fetchFollowUpUpcoming = createAsyncThunk(
  'followUp/fetchFollowUpUpcoming',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/clientsscheduledupcoming');
      return response.data;
    } catch (error) {
      return rejectWithValue(asListError(error, 'Error fetching data'));
    }
  }
);

export const fetchFollowUpPrevious = createAsyncThunk(
  'followUp/fetchFollowUpPrevious',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/clientsscheduledprevious');
      return response.data;
    } catch (error) {
      return rejectWithValue(asListError(error, 'Error fetching data'));
    }
  }
);

export const applyFollowUpCountryFilter = createAsyncThunk(
  'followUp/applyFollowUpCountryFilter',
  async ({ selectedLeads, updateComingState, previousState }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/followupCountryFilter', {
        selectedLeads,
        updateComingState,
        previousState,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to apply country filter'));
    }
  }
);

export const moveToBidding = createAsyncThunk(
  'followUp/moveToBidding',
  async (rfqNo, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/move-to-bidding/${rfqNo}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to move lead to bidding'));
    }
  }
);

// ----------------------------------------------------------------- shared

export const fetchInquiryDetails = createAsyncThunk(
  'followUp/fetchInquiryDetails',
  async (rfqNo, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/inquiry-details', {
        params: { rfq_no: rfqNo },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch inquiry details'));
    }
  }
);

export const checkSolitaireCompany = createAsyncThunk(
  'followUp/checkSolitaireCompany',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/check-solitaire-company/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to check client'));
    }
  }
);

export const moveToColdCalling = createAsyncThunk(
  'followUp/moveToColdCalling',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/move-to-cold-calling', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Operation failed'));
    }
  }
);

export const updateScheduledDate = createAsyncThunk(
  'followUp/updateScheduledDate',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/update-scheduled-date', payload);
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to update scheduled date'));
    }
  }
);

export const fetchScheduledDate = createAsyncThunk(
  'followUp/fetchScheduledDate',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/get-scheduled-date/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch scheduled date'));
    }
  }
);

export const fetchReviewRemarks = createAsyncThunk(
  'followUp/fetchReviewRemarks',
  async (rfqNo, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/followup-review-remarks/${rfqNo}`);
      return { rfqNo, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch review remarks'));
    }
  }
);

export const fetchLatestReviewRemark = createAsyncThunk(
  'followUp/fetchLatestReviewRemark',
  async (rfqNo, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/followup-review-remarks-latest/${rfqNo}`);
      return { rfqNo, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch latest remark'));
    }
  }
);

const initialState = {
  leads: [],
  filteredLeads: [],
  loading: true,
  error: null,
  errorStatus: null,
};

const followUpSlice = createSlice({
  name: 'followUp',
  initialState,
  reducers: {
    // The page maps rows through its IST helpers before storing them.
    setFollowUpLeads: (state, action) => {
      state.leads = action.payload;
    },
    setFollowUpFilteredLeads: (state, action) => {
      state.filteredLeads = action.payload;
    },
    setFollowUpLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearFollowUpError: (state) => {
      state.error = null;
      state.errorStatus = null;
    },
    resetFollowUpState: () => initialState,
  },
  extraReducers: (builder) => {
    const listPending = (state) => {
      state.loading = true;
      state.error = null;
      state.errorStatus = null;
    };
    const listRejected = (state, action) => {
      state.loading = false;
      // `asListError` guarantees a string here; the `?? ` is belt-and-braces
      // against a rejection that bypassed it (e.g. a thrown non-Error).
      state.error = typeof action.payload?.message === 'string'
        ? action.payload.message
        : 'Error fetching data';
      state.errorStatus = action.payload?.status ?? null;
    };
    builder
      .addCase(fetchFollowUpDue.pending, listPending)
      .addCase(fetchFollowUpDue.rejected, listRejected)
      .addCase(fetchFollowUpUpcoming.pending, listPending)
      .addCase(fetchFollowUpUpcoming.rejected, listRejected)
      .addCase(fetchFollowUpPrevious.pending, listPending)
      .addCase(fetchFollowUpPrevious.rejected, listRejected);
  },
});

export const {
  setFollowUpLeads,
  setFollowUpFilteredLeads,
  setFollowUpLoading,
  clearFollowUpError,
  resetFollowUpState,
} = followUpSlice.actions;

export default followUpSlice.reducer;
